/**
 * Detect and recover from stale JS/CSS chunk loads after a deploy.
 * Long-lived tabs often keep an old shell that references hashed assets
 * that no longer exist on the CDN.
 */

const RELOAD_AT_KEY = 'tm:chunk-reload-at';
/** Prevent infinite reload loops if the shell itself is still broken */
const RELOAD_COOLDOWN_MS = 15_000;

const CHUNK_LOAD_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Loading chunk [\w-]+ failed/i,
  /Unable to preload CSS/i,
  /ChunkLoadError/i,
  /^Load failed$/i, // Safari network/module load failures
];

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;

  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : String(error);

  if (CHUNK_LOAD_PATTERNS.some((pattern) => pattern.test(message))) {
    return true;
  }

  // Vite/React lazy() sometimes wraps the cause (Error.cause is ES2022+)
  if (error instanceof Error) {
    const cause = (error as Error & { cause?: unknown }).cause;
    if (cause) return isChunkLoadError(cause);
  }

  const name = error instanceof Error ? error.name : '';
  return name === 'ChunkLoadError';
}

export function hasAlreadyReloadedForChunkError(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_AT_KEY) || 0);
    if (!last) return false;
    return Date.now() - last < RELOAD_COOLDOWN_MS;
  } catch {
    return false;
  }
}

/**
 * One-shot hard reload so the browser picks up the latest index.html + chunks.
 * Returns true if a reload was triggered.
 */
export function tryReloadOnceForChunkError(): boolean {
  if (typeof window === 'undefined') return false;
  if (hasAlreadyReloadedForChunkError()) return false;

  try {
    sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()));
  } catch {
    // sessionStorage may be blocked; still attempt a single reload
  }

  // Bust any stale document cache on the way back in
  const url = new URL(window.location.href);
  url.searchParams.set('_reload', String(Date.now()));
  window.location.replace(url.toString());
  return true;
}

export function clearChunkReloadFlag(): void {
  try {
    sessionStorage.removeItem(RELOAD_AT_KEY);
  } catch {
    // ignore
  }
}
